import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { createSharedComposable } from '@vueuse/core';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import { useFramework } from '@/modules/framework/api';
import type { Answer } from '@@/packages/shared/types/models/answer';
import type { ScopingAnswer } from '@@/packages/shared/types/models/scopingAnswer';
import type { ControlBookmark } from '@@/packages/shared/types/models/control';
import type { PolicyRequirement } from '@@/packages/shared/types/models/policy';
import { DOCUMENTS_QUERY } from '@/modules/documents/api/queryKeys';
import { CLIENT_ASSESSMENT_ANSWERS_QUERY } from '@/modules/profile/api/queryKeys';
import { useStore } from '@/store/useStore';
import {
  ASSESSMENT_ANSWER_QUERY,
  ASSESSMENT_ANSWERS_QUERY,
  CONTROL_BOOKMARKS_QUERY,
  NEW_IN_SCOPE_DOMAINS_QUERY,
  POLICY_REQUIREMENTS_QUERY,
  REASSESMENT_STATUS_QUERY,
  SCOPING_ANSWERS_QUERY,
  CHANGE_SCOPING_ANSWER_MUTATION,
} from './queryKeys';

export const fetchAssessmentStatus = () => request('GET', `/assessment/status`);

type PolicyRequirementsResponse = {
  requirements: Record<string, { requirement: PolicyRequirement }>;
};

export type ScopingAnswersResponse = Record<string, ScopingAnswer>;

export const completeScopingQuestions = () =>
  request('PUT', '/assessment/scoping-answers/complete');

export type ControlAnswersResponse = Answer[];

export const confirmAnswerControlUpdate = (answerID: string) =>
  request('POST', `/assessment/answers/${answerID}/confirm-answer-control-update`);

export const submitAssessment = () => request('POST', `/assessment/complete`);

// The backend rejected the submit as incomplete, so the client's cached
// answers/status are stale — refetch to reconcile. Best-effort.
export function useReconcileAfterIncompleteSubmit() {
  const queryClient = useQueryClient();
  const store = useStore();

  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [ASSESSMENT_ANSWERS_QUERY] }),
      queryClient.invalidateQueries({ queryKey: [SCOPING_ANSWERS_QUERY] }),
      queryClient.invalidateQueries({ queryKey: [REASSESMENT_STATUS_QUERY] }),
      store.dispatch('assessment/getAssessmentStatus'),
    ]).catch(() => {});
}

export function useControlBookmarks(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CONTROL_BOOKMARKS_QUERY],
      async queryFn() {
        const res = await request<ControlBookmark[]>('GET', '/assessment/control-bookmarks');
        return res.data;
      },
    }),
  );

  const controlBookmarksByControlID = computed(() =>
    Object.fromEntries(query.data.value?.map((bookmark) => [bookmark.controlID, bookmark]) ?? []),
  );

  const isControlBookmarked = (controlID: string) =>
    controlBookmarksByControlID.value?.[controlID]?.bookmarked;

  return {
    ...query,
    controlBookmarksByControlID,
    isControlBookmarked,
  };
}

export function useBookmarkControl(options?: MutationOptions<ControlBookmark, string>) {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    mergeMutationOptions(options, {
      async mutationFn(controlID: string) {
        const res = await request<ControlBookmark>(
          'POST',
          `/assessment/control/${controlID}/bookmark`,
        );
        return res.data;
      },
      onSuccess: async (bookmark, controlID) => {
        queryClient.setQueryData<ControlBookmark[]>([CONTROL_BOOKMARKS_QUERY], (oldData) => {
          if (!oldData) return [bookmark];
          const newData = oldData.filter((b) => b.controlID !== controlID);
          // Remove the existing bookmark from the new data if it already exists
          newData.filter((b) => b.controlID !== controlID);
          // Add the new bookmark to the new data
          return [...newData, bookmark];
        });
        await queryClient.invalidateQueries({ queryKey: [CONTROL_BOOKMARKS_QUERY] });
      },
    }),
  );
  return mutation;
}

export function useUnbookmarkControl(options?: MutationOptions<ControlBookmark, string>) {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    mergeMutationOptions(options, {
      async mutationFn(controlID: string) {
        const res = await request<ControlBookmark>(
          'POST',
          `/assessment/control/${controlID}/unbookmark`,
        );
        return res.data;
      },
      onSuccess: async (_, controlID) => {
        queryClient.setQueryData<ControlBookmark[]>([CONTROL_BOOKMARKS_QUERY], (oldData) => {
          if (!oldData) return [];
          return oldData.filter((b) => b.controlID !== controlID);
        });
        await queryClient.invalidateQueries({ queryKey: [CONTROL_BOOKMARKS_QUERY] });
      },
    }),
  );
  return mutation;
}

// Derived state + Vuex sync for scoping answers, shared once across all callers
// rather than rebuilt per-caller. Subscribes to the query cache directly instead
// of via `useQuery` so it doesn't add an always-enabled observer that would
// override per-caller `enabled` semantics. See `useSharedControlAnswers` for
// the same pattern with more context.
const useSharedScopingAnswers = createSharedComposable(() => {
  const queryClient = useQueryClient();
  const store = useStore();

  const cachedData = ref<ScopingAnswersResponse | undefined>(
    queryClient.getQueryData<ScopingAnswersResponse>([SCOPING_ANSWERS_QUERY]),
  );
  const unsubscribe = queryClient.getQueryCache().subscribe(({ query }) => {
    if (query.queryKey[0] === SCOPING_ANSWERS_QUERY) {
      cachedData.value = query.state.data as ScopingAnswersResponse | undefined;
    }
  });
  onScopeDispose(unsubscribe);

  const scopingAnswers = computed(() => cachedData.value ?? {});

  // Temporary sync with store until we have removed the assessment store (COR-809).
  watchEffect(() => {
    if (!store || !cachedData.value) return;
    store.commit('assessment/SET_SCOPING_ANSWERS', scopingAnswers.value);
  });

  const { sortedDomains, sortedControls } = useFramework();
  const totalScopingQuestions = computed(
    () => sortedDomains.value.filter((domain) => domain.scopingQuestion).length,
  );
  const totalScopingAnswered = computed(
    () => sortedDomains.value.filter((domain) => !!scopingAnswers.value[domain.domainID]).length,
  );

  const outOfScopeDomains = computed(() =>
    sortedDomains.value.filter(
      (domain) => domain.scopingQuestion && scopingAnswers.value[domain.domainID]?.answer === false,
    ),
  );

  const outOfScopeControlIDs = computed(() =>
    sortedControls.value
      .filter((control) =>
        outOfScopeDomains.value.some((domain) => domain.domainID === control.domainID),
      )
      .map((control) => control.controlID),
  );

  const isDomainOutOfScope = (domainID: string) =>
    outOfScopeDomains.value.some((domain) => domain.domainID === domainID);

  const isControlOutOfScope = (controlID: string) => outOfScopeControlIDs.value.includes(controlID);

  return {
    scopingAnswers,
    totalScopingQuestions,
    totalScopingAnswered,
    outOfScopeDomains,
    isDomainOutOfScope,
    isControlOutOfScope,
  };
});

export function useScopingAnswers(options?: QueryOptions) {
  // Each caller gets its own observer so per-caller options (e.g. `enabled`)
  // still apply; the derived state and Vuex sync are shared via
  // useSharedScopingAnswers.
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SCOPING_ANSWERS_QUERY],
      async queryFn() {
        const res = await request<ScopingAnswersResponse>('GET', '/assessment/scoping-answers');
        return res.data;
      },
      // RL-7478: set stale time to 30s to avoid potential re-mounting race
      // condition with the vuex store sync
      staleTime: 30_000,
    }),
  );

  return { ...query, query, ...useSharedScopingAnswers() };
}

type ChangeScopingAnswerPayload = Pick<ScopingAnswer, 'domainID' | 'answer' | 'notes'>;

export function useChangeScopingAnswer(
  options?: MutationOptions<ScopingAnswer, ChangeScopingAnswerPayload>,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    mergeMutationOptions(options, {
      mutationKey: [CHANGE_SCOPING_ANSWER_MUTATION],
      async mutationFn({ domainID, answer, notes }: ChangeScopingAnswerPayload) {
        const res = await request<ScopingAnswer>('PUT', `/assessment/scoping-answers/${domainID}`, {
          answer,
          notes,
        });
        return res.data;
      },
      onSuccess: async (scopingAnswer) => {
        queryClient.setQueryData<Record<string, ScopingAnswer>>(
          [SCOPING_ANSWERS_QUERY],
          (oldData) => {
            if (!oldData) return { [scopingAnswer.domainID]: scopingAnswer };
            return { ...oldData, [scopingAnswer.domainID]: scopingAnswer };
          },
        );
        await queryClient.invalidateQueries({ queryKey: [SCOPING_ANSWERS_QUERY] });
      },
    }),
  );

  return mutation;
}

// The scoping answers query only reflects an answer once its save has settled
export function useIsSavingScopingAnswer() {
  const savesInFlight = useIsMutating({ mutationKey: [CHANGE_SCOPING_ANSWER_MUTATION] });
  return computed(() => savesInFlight.value > 0);
}

// Derived state + Vuex sync for control answers, shared once across all callers
// rather than rebuilt per-caller. The assessment page mounts ~13 useControlAnswers
// callers; without sharing, the parent/child fixup in `controlAnswers` would
// run N times per data change and the Vuex sync would fan out to N redundant
// commits, triggering a render cascade across all consumers (visible as
// thrashing during typing because every save triggers a refetch).
//
// Subscribes to the query cache directly instead of via `useQuery` so it
// doesn't add an always-enabled observer that would override per-caller
// `enabled` semantics.
const useSharedControlAnswers = createSharedComposable(() => {
  const queryClient = useQueryClient();
  const { dependentControls, controlWithID } = useFramework();
  const store = useStore();

  const cachedData = ref<ControlAnswersResponse | undefined>(
    queryClient.getQueryData<ControlAnswersResponse>([ASSESSMENT_ANSWERS_QUERY]),
  );
  const unsubscribe = queryClient.getQueryCache().subscribe(({ query }) => {
    if (query.queryKey[0] === ASSESSMENT_ANSWERS_QUERY) {
      cachedData.value = query.state.data as ControlAnswersResponse | undefined;
    }
  });
  onScopeDispose(unsubscribe);

  // ALWAYS use this instead of query.data, as this has an eventual consistency fix
  const controlAnswers = computed(
    () =>
      cachedData.value?.map((answer) => {
        const control = controlWithID(answer.controlID);

        // This horrific code is to work around an eventual consistency issue
        // on updating a control where child controls might not have been
        // correctly marked as not applicable yet as it happens on an event
        if (
          control.parentID &&
          dependentControls.value[control.parentID]?.includes(control.controlID)
        ) {
          const parentAnswer = cachedData.value?.find((otherAnswer) => {
            if (answer.productID) {
              return (
                otherAnswer.controlID === control.parentID &&
                otherAnswer.productID === answer.productID
              );
            }

            return otherAnswer.controlID === control.parentID && !otherAnswer.productID;
          });

          if (!parentAnswer) return answer;

          let notApplicable: boolean;
          try {
            const parsedAnswer = JSON.parse(parentAnswer.answer);

            // answer is stringified JSON e.g. for YesCompliantWithExpiryControl control type
            notApplicable = control.ifParentAnswer !== parsedAnswer.answer;
          } catch {
            // answer is not stringified JSON
            notApplicable = control.ifParentAnswer !== parentAnswer.answer;
          }

          return { ...answer, notApplicable };
        }
        return answer;
      }) || [],
  );

  /** @deprecated use controlAnswersByControlID instead */
  const orgAnswerOnlyByControlID = computed(() =>
    Object.fromEntries(
      controlAnswers.value
        .filter((answer) => !answer.productID)
        .map((answer) => [answer.controlID, answer]),
    ),
  );

  const controlAnswersByControlID = computed(() =>
    Object.groupBy(controlAnswers.value, (answer) => answer.controlID),
  );

  // Temporary sync with store until we have removed the assessment store (COR-809).
  watchEffect(() => {
    if (!store || !cachedData.value) return;
    store.commit('assessment/SET_CONTROL_ANSWERS', orgAnswerOnlyByControlID.value);
  });

  function getByAnswerID(answerID: string) {
    for (const controlAnswer of controlAnswers.value) {
      if (controlAnswer.answerID === answerID) {
        return controlAnswer;
      }
    }
    return null;
  }

  return {
    controlAnswers,
    orgAnswerOnlyByControlID,
    controlAnswersByControlID,
    getByAnswerID,
  };
});

export function useControlAnswers(options?: QueryOptions) {
  // Each caller gets its own observer so per-caller options (e.g. `enabled`)
  // still apply; the derived state and Vuex sync are shared via
  // useSharedControlAnswers.
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ASSESSMENT_ANSWERS_QUERY],
      async queryFn() {
        const res = await request<ControlAnswersResponse>('GET', '/assessment/answers/v2');
        return res.data;
      },
    }),
  );

  return { ...query, ...useSharedControlAnswers() };
}

// Fetches a single answer by ID. Unlike the assessment-answers list endpoints,
// this returns archived answers too — used to surface PLA context on risks
// and discussions whose answer has since been archived. Archived answers are
// effectively immutable while archived, so a small staleTime keeps repeated
// side-panel opens from refetching.
export function useAnswer(
  answerID: MaybeRefOrGetter<string | null | undefined>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ASSESSMENT_ANSWER_QUERY, toRef(answerID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<Answer>('GET', `/assessment/answers/${queryKey[1]}`);
        return res.data;
      },
      enabled: () => !!toValue(answerID),
      staleTime: 30_000,
    }),
  );

  return { ...query, answer: query.data };
}

export function usePolicyRequirements(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [POLICY_REQUIREMENTS_QUERY],
      async queryFn() {
        const res = await request<PolicyRequirementsResponse>(
          'GET',
          '/assessment/policy-requirements',
        );
        return res.data;
      },
    }),
  );

  const policyRequirements = computed(() => query.data.value ?? ({} as PolicyRequirementsResponse));

  const getControlRequirements = (controlID: string) =>
    policyRequirements.value.requirements?.[controlID]?.requirement;

  return {
    ...query,
    policyRequirements,
    getControlRequirements,
  };
}

export function useConfirmScopingAnswer(options?: MutationOptions<ScopingAnswer, string>) {
  const store = useStore();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(domainId) {
        const res = await request(
          'POST',
          `/assessment/scoping-answers/${domainId}/confirm-control-update`,
        );
        return res.data;
      },
      onSuccess(data) {
        store.commit('assessment/CHANGE_SCOPING_ANSWER', data);
      },
    }),
  );
}

export function useNewInscopeDomains(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [NEW_IN_SCOPE_DOMAINS_QUERY],
      async queryFn() {
        const res = await request<string[]>('GET', '/quick-answer/new-in-scope-domains');
        return res.data;
      },
    }),
  );

  return { ...query, newInscopeDomains: computed(() => query.data.value || []) };
}

function sameStringSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

export function useAnswerControl(
  options?: MutationOptions<Answer, { controlID: string; answer: Partial<Answer> }>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ controlID, answer }) {
        const res = await request<Answer>('PUT', `/assessment/answers/${controlID}`, answer);
        return res.data;
      },
      async onSuccess(data) {
        // Compare against the cached previous answer (if any) to decide
        // whether the documents query needs refetching.
        const prev = queryClient
          .getQueryData<Answer[]>([ASSESSMENT_ANSWERS_QUERY])
          ?.find((a) => a.answerID === data.answerID);
        const documentsChanged = prev
          ? !sameStringSet(prev.documentIDs, data.documentIDs)
          : data.documentIDs.length > 0;

        await queryClient.setQueryData(
          [ASSESSMENT_ANSWERS_QUERY],
          (oldData: Answer[] | undefined) => {
            if (!oldData) return undefined;
            const prevAnswerIndex = oldData.findIndex((a) => a.answerID === data.answerID);
            if (prevAnswerIndex !== -1) {
              oldData[prevAnswerIndex] = data;
            } else {
              oldData.push(data);
            }
            return oldData;
          },
        );

        await Promise.all([
          documentsChanged
            ? queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY] })
            : Promise.resolve(),
          queryClient.invalidateQueries({ queryKey: [ASSESSMENT_ANSWERS_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_ASSESSMENT_ANSWERS_QUERY] }),
        ]);
      },
    }),
  );
}

export function useRemoveControlAnswer(options?: MutationOptions<ControlAnswersResponse, string>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(controlID) {
        const res = await request('PUT', `/assessment/answers/${controlID}/remove`);
        return res.data;
      },
      async onSuccess(_data) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [ASSESSMENT_ANSWERS_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_ASSESSMENT_ANSWERS_QUERY] }),
        ]);
      },
    }),
  );
}

export function useArchiveProductAnswer(
  options?: MutationOptions<null, { productID: string; answerID: string }>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ productID, answerID }) {
        const res = await request(
          'PUT',
          `/assessment/product/${productID}/answers/${answerID}/archive`,
        );
        return res.data;
      },
      async onSuccess(_data, { answerID }) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [ASSESSMENT_ANSWERS_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_ASSESSMENT_ANSWERS_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [ASSESSMENT_ANSWER_QUERY, answerID] }),
        ]);
      },
    }),
  );
}
